<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="true"
    location="bottom end"
    origin="top end"
    transition="scale-transition"
    min-width="140"
    content-class="language-switcher-menu"
  >
    <template #activator="{ props }">
      <v-btn
        v-bind="props"
        :variant="variant"
        size="small"
        class="language-switcher-trigger text-none"
        rounded="lg"
        :aria-label="t('app.language')"
      >
        <span class="trigger-flag" :class="{ 'trigger-flag-only': compact }" aria-hidden="true">{{
          currentLanguage?.flag
        }}</span>
        <template v-if="!compact">
          <span class="trigger-label">{{
            currentLanguage?.shortLabel ?? currentLanguage?.name
          }}</span>
          <v-icon size="18" class="trigger-chevron" :class="{ open: menuOpen }">
            mdi-chevron-down
          </v-icon>
        </template>
        <v-icon v-else size="16" class="trigger-chevron" :class="{ open: menuOpen }">
          mdi-chevron-down
        </v-icon>
      </v-btn>
    </template>
    <v-list class="language-switcher-list" density="compact">
      <v-list-item
        v-for="lang in availableLanguages"
        :key="lang.code"
        :active="currentLocale === lang.code"
        class="language-option"
        rounded="lg"
        @click="changeLanguage(lang.code)"
      >
        <template #prepend>
          <span class="option-flag" aria-hidden="true">{{ lang.flag }}</span>
        </template>
        <v-list-item-title class="option-title">{{ lang.name }}</v-list-item-title>
        <template v-if="currentLocale === lang.code" #append>
          <v-icon size="16" color="primary">mdi-check</v-icon>
        </template>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface LanguageOption {
  code: string;
  name: string;
  shortLabel: string;
  flag: string;
}

const { locale, t } = useI18n();
const menuOpen = ref(false);

const availableLanguages: LanguageOption[] = [
  { code: 'en', name: 'English', shortLabel: 'EN', flag: '🇬🇧' },
  { code: 'es', name: 'Español', shortLabel: 'ES', flag: '🇪🇸' },
];

const currentLocale = computed(() => locale.value);

const currentLanguage = computed(
  () => availableLanguages.find((l) => l.code === locale.value) ?? availableLanguages[0]
);

const changeLanguage = (langCode: string) => {
  locale.value = langCode;
  menuOpen.value = false;
  localStorage.setItem('preferredLanguage', langCode);
};

withDefaults(
  defineProps<{
    variant?: 'text' | 'flat' | 'outlined' | 'tonal' | 'plain' | 'elevated';
    /** When true, only the current language flag is shown (e.g. for app bar). */
    compact?: boolean;
  }>(),
  { variant: 'tonal', compact: false }
);
</script>

<style scoped>
.language-switcher-trigger {
  letter-spacing: 0.02em;
  font-weight: 500;
}

.trigger-flag {
  font-size: 1.1rem;
  line-height: 1;
  margin-right: 6px;
}

.trigger-flag-only {
  margin-right: 4px;
  font-size: 1.25rem;
}

.trigger-label {
  margin-right: 2px;
}

.trigger-chevron {
  transition: transform 0.2s ease;
}

.trigger-chevron.open {
  transform: rotate(180deg);
}

.option-flag {
  font-size: 1rem;
  line-height: 1;
  margin-right: 8px;
  min-width: 1.25rem;
  text-align: center;
}

.option-title {
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.language-option {
  margin: 0 4px;
  min-height: 36px;
}

.language-option:first-child {
  margin-top: 2px;
}

.language-option:last-child {
  margin-bottom: 2px;
}
</style>

<style>
.language-switcher-menu.v-overlay-container .v-overlay__content {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.06);
}

.language-switcher-list {
  padding: 4px 0 !important;
}
.language-switcher-list .v-list-item {
  padding-inline-start: 10px !important;
  padding-inline-end: 10px !important;
}
</style>
